import { Game } from "../../game.js";
import { TransformOptions, UIElement } from "./ui_element.js";

export interface BlurredShadowBlobOptions
{
  color?: string;
  opacity?: number;
  blur?: number;
}

export class BlurredShadowBlob extends UIElement
{
  private color: string;
  private blob_opacity: number;
  private blur: number;

  private cache?: HTMLCanvasElement;
  private cache_key = "";
  private cache_scale = 1;
  private cache_pad = 0;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    options: BlurredShadowBlobOptions = {},
    ui_options?: TransformOptions
  )
  {
    super( ctx, x, y, width, height, false, false, ui_options );

    this.color = options.color ?? "#000000";
    this.blob_opacity = options.opacity ?? 0.5;
    this.blur = options.blur ?? 10;
  }

  private ensureCache( w: number, h: number ): HTMLCanvasElement | undefined
  {
    const scale = Game.getDpr();
    const pad = Math.ceil( this.blur * 3 );
    const key = `${w}|${h}|${this.blur}|${this.color}|${scale}`;

    if ( this.cache && this.cache_key === key )
      return this.cache;

    const cw = Math.ceil( ( w + pad * 2 ) * scale );
    const ch = Math.ceil( ( h + pad * 2 ) * scale );
    if ( cw <= 0 || ch <= 0 )
      return undefined;

    const canvas = document.createElement( "canvas" );
    canvas.width = cw;
    canvas.height = ch;

    const cctx = canvas.getContext( "2d" );
    if ( !cctx )
      return undefined;

    cctx.scale( scale, scale );
    cctx.filter = `blur(${this.blur}px)`;
    cctx.fillStyle = this.color;
    cctx.beginPath();
    cctx.ellipse( pad + w / 2, pad + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2 );
    cctx.fill();

    this.cache = canvas;
    this.cache_key = key;
    this.cache_scale = scale;
    this.cache_pad = pad;

    return canvas;
  }

  private dom_last_key = "";

  protected override syncDomContent( node: HTMLElement ): boolean
  {
    const key = `${this.color}|${this.blob_opacity}|${this.blur}`;
    if ( key === this.dom_last_key )
      return true;
    this.dom_last_key = key;
    const s = node.style;
    s.background = this.color;
    s.borderRadius = "50%";
    s.filter = `blur(${this.blur}px) opacity(${this.blob_opacity})`;
    return true;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();

    const w = this.rect.getW();
    const h = this.rect.getH();

    if ( w > 0 && h > 0 && !this.dom_mode )
    {
      const cache = this.ensureCache( w, h );
      if ( cache )
      {
        const ctx = this.ctx;
        ctx.save();
        ctx.globalAlpha *= this.blob_opacity;
        ctx.drawImage(
          cache,
          -w / 2 - this.cache_pad,
          -h / 2 - this.cache_pad,
          cache.width / this.cache_scale,
          cache.height / this.cache_scale
        );
        ctx.restore();
      }
    }

    this.finishRender();
  }

  public name(): string
  {
    return "BlurredShadowBlob";
  }
}
